import { exportDataGrid as exportDataGridToExcel } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Column } from "devextreme/ui/data_grid";
import notify from "devextreme/ui/notify";
import dxTooltip from "devextreme/ui/tooltip";
import * as ExcelJS from "exceljs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { api } from "../../modules/Axios/api";
import {
    MENSAGEM_ERRO_GENERICA,
    sanitizeErrorMessage,
} from "../../utils/sanitizeErrorMessage";

/**
 * @typedef {import('moment').Moment} Moment
 */

export const shortHumanDateTime = (
    value,
    placeholder = false,
    format = "DD/MM HH:mm",
) => {
    const dateTime = moment(value).format(format);

    return value ? dateTime : placeholder || dateTime;
};

/**
 * Transforma o inteiro em HH:MM, se estiver em branco retorna a string "—"
 * @param {string|number} minutos
 * @returns {string}
 */
export const minutesToHHMM = (minutos) => {
    if (
        (typeof minutos === "number" && minutos > 0) ||
        (typeof minutos === "string" && minutos !== "")
    ) {
        const hours = minutos / 60;
        const rhours = Math.floor(hours);
        const minutes = (hours - rhours) * 60;
        let rminutes = Math.round(minutes);
        if (rminutes === 60) {
            rminutes = 59;
        }
        return (
            rhours.toString().padStart(2, "0") +
            ":" +
            rminutes.toString().padStart(2, "0")
        );
    }
    return "—";
};

export const minutosParaHHMMNegativo = (minutos) => {
    if (typeof minutos !== "number") {
        return "—";
    }

    let minutos_pos = minutos < 0 ? minutos * -1 : minutos;

    let hrs = Math.floor(minutos_pos / 60);
    let mins = minutos_pos % 60;

    let result =
        hrs.toString().padStart(2, "0") +
        ":" +
        mins.toString().padStart(2, "0");

    return minutos < 0 ? `-${result}` : result;
};

/**
 * @param {dxElement} container
 * @param {{column?: Column, columnIndex?: number, component?: dxDataGrid}} data
 */
export const wrapHeader = (container, data) => {
    let texto = "";
    const textoQuebrado = data.column.caption.split(" ");
    // container.style.setProperty("font-size", "8px")

    for (let i = 0; i < textoQuebrado.length; i++) {
        texto += textoQuebrado[i] + " ";
        if (i % 2 === 1 || textoQuebrado.length === 2 || i === 2) texto += "\n";
    }

    const div = $("<div/>").text(texto);

    div.html(div.html().replace(/\n/g, "<br/>"));
    div.appendTo(container);
};

export const checkForAlert = (data) => {
    if (!data) return;
    const { message, type, displayTime } = data;
    if (!message) return;
    new notify(message, type ?? "info", displayTime ?? 5000);
    return true;
};

/**
 * @param {Error|string} error
 */
export const handleError = (
    error,
    rethrowError = true,
    fallbackMessage = "Erro ao processar os dados",
) => {
    let message =
        error?.response?.data?.error?.message ??
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.details ??
        error?.response?.data?.detail ??
        error?.message ??
        error;

    // Garante um texto legível no toast. Quando o backend responde apenas com um
    // objeto sem `.message` (ex.: `{ error: {} }`, gerado quando o controller cai
    // em `response.errorMessage` — undefined), as opções acima resolvem para um
    // objeto e o toast exibia o literal "[object Object]". Nesse caso usamos o
    // fallback amigável e registramos o erro completo no console para depuração.
    if (typeof message !== "string" || !message.trim()) {
        if (message != null && typeof message !== "string") {
            console.error("handleError - erro sem mensagem legível:", error);
        }
        message = fallbackMessage;
    }

    // Falha de infraestrutura chega aqui com host, usuário e base de dados no
    // texto (ex.: "failed to connect to `host=… user=postgres database=hera`").
    // O toast exibiria isso literalmente, então o texto cru fica no console e o
    // usuário vê a mensagem genérica.
    message = sanitizeErrorMessage(message, MENSAGEM_ERRO_GENERICA);

    notify(
        {
            message,
            closeOnClick: true,
        },
        "error",
        60000,
    );
    if (rethrowError) {
        if (error instanceof Error) throw error;
        throw new Error(message);
    }
};

/**
 * @param {string} texto
 * @return {string}
 */
export const primeiraMaiuscula = (texto) =>
    texto.charAt(0).toUpperCase() + texto.slice(1);

/**
 * Converte a data para o formato local com o fuso atual
 * @param {Date} date
 */
export const dateToLocal = (date) =>
    date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });

/**
 * Converte a data para o formato local com o fuso atual
 * @param {Date} date
 */
export const dateToDateLocal = (date) =>
    date.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });

/**
 * Converte a data para o formato de data do SQL YYYY-MM-DD com o fuso atual
 * @param {Date} date
 */
export const dateToDateSQL = (date) =>
    date.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });

/**
 * Converte a data para o formato de data do SQL YYYY-MM-DD HH:MM:SS com o fuso atual
 * @param {Date} date
 */
export const dateToSQL = (date) =>
    date.toLocaleString("sv-SE", { timeZone: "America/Sao_Paulo" });

/**
 * Deleta pelo store as linhas selecionadas da grid
 * @param {dxDataGrid} grid
 * @param {string} additionalData
 */
export const deleteSelectedRows = async (grid, additionalData = undefined) => {
    const store = grid.getDataSource().store();
    const chaves = grid.getSelectedRowKeys();

    for (const chave of chaves) {
        const key = additionalData
            ? `${chave}/${encodeURI(additionalData)}`
            : chave;
        await store.remove(key);
    }

    grid.clearSelection();
};

/**
 * Utilia o moment para retornar a diferença entre duas datas em minutos
 * @param {Moment} start
 * @param {Moment} end
 * @param {boolean} [ignoreSeconds]
 * @returns {number}
 */
export const minutesDiff = (start, end, ignoreSeconds) => {
    if (ignoreSeconds) {
        start.set({ seconds: 0 });
        end.set({ seconds: 0 });
    }

    // Verifica se o tempo de término é 23:59 e adiciona um minuto
    if (end.hour() === 23 && end.minute() === 59) {
        end.add(1, "minute");
    }

    const duration = moment.duration(end.diff(start));

    return duration.asMinutes();
};

/**
 * Ajusta a formatação do filtro de data quando vem no formato YYYY/MM/DD
 * É utilizado para tratar o filtro da coluna última posição da gestão da jornada
 * @param {*[]} array
 */
export const ajustaFormatoFiltroData = (array) => {
    for (let i = 0; i < array.length; i++) {
        if (Array.isArray(array[i])) {
            ajustaFormatoFiltroData(array[i]);
        } else {
            const data = moment(array[i], "YYYY/MM/DD HH:mm:ss", true);
            const dataValida = data.isValid();
            if (dataValida && typeof array[i] === "string") {
                array[i] = data.format("YYYY-MM-DD HH:mm:ss");
            }
        }
    }
};
/**
 * @param token
 * @return {null|{CODIGO_USUARIO: string, CODIGO_CLIENTE: string}}
 */
export const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split(" ")[1].split(".")[1]));
    } catch (e) {
        return null;
    }
};

/**
 * Agrupa duas listas de mesmo tamanho
 * @param {[*]} lista1
 * @param {[*]} lista2
 * @return [[*]]
 */
export const zipSameSize = (lista1, lista2) =>
    lista1.map((val, idx) => [val, lista2[idx]]);

/**
 * Agrupa duas listas de tamanhos diferentes
 * @param {[*]} lista1
 * @param {[*]} lista2
 * @return [[*]]
 */
export const zipDiffSize = (lista1, lista2) =>
    Array.from(Array(Math.max(lista2.length, lista1.length)), (_, i) => [
        lista1[i],
        lista2[i],
    ]);

/**
 * Padroniza os botões de incluir da dataGrid
 * @param {string} texto - Texto do botão
 * @param {{}} [additional] - Opções adicionais
 * @param {{}} [widgetOptions] - Opções adicionais do widget
 */
export const personalizarBotaoIncluir = (texto, additional, widgetOptions) => ({
    options: {
        text: "Incluir",
        hint: texto,
        elementAttr: { class: "botao-incluir-nova" },
        ...widgetOptions,
    },
    showText: "always",
    cssClass: "vertical-bottom",
    ...additional,
});

/**
 * Validação padrão de obrigatoriedade
 * @type {[{type: string, message: string}]}
 */
export const validation = [
    {
        type: "required",
        message: "Campo Obrigatório",
    },
];

/**
 * Enum dos eventos
 * @type {{DIRECAO: 990, REFEICAO: 992,DESCANSO: 991, REPOUSO: 993, ESPERA: 994, PARADO_EM_JORNADA: 995, EMBARQUE: 996, INICIO_DIRECAO: 997, FIM_DIRECAO: 998, DESEMBARQUE: 999, SEM_POSICAO: 1000}}
 */
export const EVENTOS = {
    DIRECAO: 990,
    DESCANSO: 991,
    REFEICAO: 992,
    REPOUSO: 993,
    ESPERA: 994,
    PARADO_EM_JORNADA: 995,
    EMBARQUE: 996,
    INICIO_DIRECAO: 997,
    FIM_DIRECAO: 998,
    DESEMBARQUE: 999,
    SEM_POSICAO: 1000,
};

/**
 * Enum dos tipos de eventos
 * @type {{ PADRAO: 0, ASSOCIACAO: 1, SEM_POSICAO: 2}}
 */
export const TIPOS_EVENTO = {
    PADRAO: 0,
    ASSOCIACAO: 1,
    SEM_POSICAO: 2,
};

/**
 * Enum dos tipos de alvo
 * @type {{ ESPERA: 0, PARADO_EM_JORNADA: 1, BALSA: 2, SEM_CONTROLE: 3, IGNORA_DIARIAS: 4}}
 */
export const TIPOS_ALVO = {
    ESPERA: 0,
    PARADO_EM_JORNADA: 1,
    BALSA: 2,
    SEM_CONTROLE: 3,
    IGNORA_DIARIAS: 4,
    CONTROLA_ENVIO_DE_MACROS: 5,
};

/**
 * Enum dos tipos jornada dos motorista
 * @type {{ NENHUM: 0, EVENTOS: 1, EMBARQUE_DESEMBARQUE: 2, EVENTOS_POR_MACRO: 3}}
 */
export const TIPOS_JORNADA_MOTORISTA = {
    NENHUM: 0,
    EVENTOS: 1,
    EMBARQUE_DESEMBARQUE: 2,
    EVENTOS_POR_MACRO: 3,
};

/**
 * Array com os eventos da jev
 * @type {[{jev_codigo: number, jev_descricao: string, jev_tipo: number, classe?: string}]}
 */
export const dataSourceJev = [
    {
        jev_codigo: EVENTOS.DIRECAO,
        jev_descricao: "EM DIREÇÃO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-direcao",
    },
    {
        jev_codigo: EVENTOS.DESCANSO,
        jev_descricao: "EM DESCANSO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-descanso",
    },
    {
        jev_codigo: EVENTOS.REFEICAO,
        jev_descricao: "EM REFEIÇÃO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-refeicao",
    },
    {
        jev_codigo: EVENTOS.REPOUSO,
        jev_descricao: "EM REPOUSO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-repouso",
    },
    {
        jev_codigo: EVENTOS.ESPERA,
        jev_descricao: "EM ESPERA",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-espera",
    },
    {
        jev_codigo: EVENTOS.PARADO_EM_JORNADA,
        jev_descricao: "PARADO EM JORNADA",
        jev_tipo: TIPOS_EVENTO.PADRAO,
    },
    {
        jev_codigo: EVENTOS.EMBARQUE,
        jev_descricao: "EMBARQUE DE MOTORISTA",
        jev_tipo: TIPOS_EVENTO.ASSOCIACAO,
    },
    {
        jev_codigo: EVENTOS.INICIO_DIRECAO,
        jev_descricao: "SOMENTE ASSOCIAR",
        jev_tipo: TIPOS_EVENTO.ASSOCIACAO,
    },
    {
        jev_codigo: EVENTOS.FIM_DIRECAO,
        jev_descricao: "DESASSOCIAR",
        jev_tipo: TIPOS_EVENTO.ASSOCIACAO,
    },
    {
        jev_codigo: EVENTOS.DESEMBARQUE,
        jev_descricao: "DESEMBARQUE DE MOTORISTA",
        jev_tipo: TIPOS_EVENTO.ASSOCIACAO,
    },
    {
        jev_codigo: EVENTOS.SEM_POSICAO,
        jev_descricao: "SEM POSICAO",
        jev_tipo: TIPOS_EVENTO.SEM_POSICAO,
        classe: "text-sem-posicao",
    },
];

export const dataSourceJevQueGeramEventos = [
    {
        jev_codigo: EVENTOS.DIRECAO,
        jev_descricao: "EM DIREÇÃO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-direcao",
    },
    {
        jev_codigo: EVENTOS.DESCANSO,
        jev_descricao: "EM DESCANSO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-descanso",
    },
    {
        jev_codigo: EVENTOS.REFEICAO,
        jev_descricao: "EM REFEIÇÃO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-refeicao",
    },
    {
        jev_codigo: EVENTOS.REPOUSO,
        jev_descricao: "EM REPOUSO",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-repouso",
    },
    {
        jev_codigo: EVENTOS.ESPERA,
        jev_descricao: "EM ESPERA",
        jev_tipo: TIPOS_EVENTO.PADRAO,
        classe: "text-espera",
    },
    {
        jev_codigo: EVENTOS.PARADO_EM_JORNADA,
        jev_descricao: "PARADO EM JORNADA",
        jev_tipo: TIPOS_EVENTO.PADRAO,
    },
];

/**
 * Array com as NCs, sem a NC 136 ("Falta de posições"), que saiu das exibições em
 * caráter temporário (JOR4-7610). O backend continua gerando a 136.
 * @type {{item: number, descricao: string}[]}
 */
export const dataSourceJov = [
    { item: 130, descricao: "DIRECAO ININTERRUPTA" },
    { item: 131, descricao: "EXCESSO DE JORNADA" },
    { item: 132, descricao: "NAO CUMPRIMENTO DO INTERVALO DE JORNADAS" },
    { item: 133, descricao: "DIRECAO NOTURNA" },
    { item: 134, descricao: "EXCESSO DE VELOCIDADE" },
    { item: 135, descricao: "JORNADA SEM PARADA PARA REFEICAO" },
];

/**
 * Array com as NCs incluindo a 136, para as telas que configuram o que o backend faz
 * com ela — processo de e-mail, NCs no app e assinatura eletrônica. Sem o item, a
 * configuração de quem já a tinha marcada seria descartada em silêncio.
 * @type {{item: number, descricao: string}[]}
 */
export const dataSourceJovComSemPosicao = [
    ...dataSourceJov,
    { item: 136, descricao: "VEÍCULO SEM POSIÇÃO A MAIS DE 5 HORAS" },
];

/**
 * Array com as Diárias.
 * A propriedade `sigla` é usada exclusivamente para exibição compacta na barra
 * de totalizadores; a `descricao` completa é mantida para tooltips e demais usos.
 * @type {{item: String, sigla: string, descricao: string}[]}
 */
export const dataSourceJdi = [
    { item: "Inteira", sigla: "I", descricao: "Inteira" },
    { item: "Meia", sigla: "M", descricao: "Meia" },
    { item: "BF", sigla: "C", descricao: "Café" },
    { item: "LU", sigla: "A", descricao: "Almoço" },
    { item: "DI", sigla: "J", descricao: "Jantar" },
    { item: "ME", sigla: "R", descricao: "Refeição" },
    { item: "ON", sigla: "P", descricao: "Pernoite" },
    {
        item: "NaoTrabalhadoForaDaCidadeBase",
        sigla: "NTF",
        descricao: "Não trabalhado fora da cidade base",
    },
    {
        item: "NaoTrabalhadoNaCidadeBase",
        sigla: "NTC",
        descricao: "Não trabalhado na cidade base",
    },
    {
        item: "Trabalhado",
        sigla: "T",
        descricao: "Trabalhado",
    },
    { item: "Diaria", sigla: "D", descricao: "Diária" },
];

/**
 * Array com as diárias por tempo
 * @type {{item: String, descricao: string}[]}
 */
export const dataSourceJdiTempo = dataSourceJdi.slice(0, 2);

/**
 * Array com as diárias por refeição
 * @type {{item: String, descricao: string}[]}
 */
export const dataSourceJdiRefeicao = dataSourceJdi.slice(2);

export const getDiariaLabel = (diaria) => {
    const daily = dataSourceJdi.find(
        (item) => item.item == diaria.tipo_faixa || item.item == diaria,
    )?.descricao;

    return daily ?? "";
};

export const convertDiariasLabel = (diarias) => {
    if (containsAll(diarias, ["BF", "LU", "DI", "ME", "ON", "Diaria"])) {
        return `Todos (${diarias.map((diaria) => getDiariaLabel(diaria).toLowerCase()).join(", ")})`;
    }

    return diarias
        .map((diaria) => getDiariaLabel(diaria).toLowerCase())
        .join(" e ")
        .capitalize();
};

/**
 * Operações de log
 * @type {[{id: number, descricao: string},{id: number, descricao: string},{id: number, descricao: string}]}
 */
export const operacoes = [
    // {id: 1, descricao: "Leitura"},
    { id: 2, descricao: "Inclusão" },
    { id: 3, descricao: "Alteração" },
    { id: 4, descricao: "Exclusão" },
];

/**
 * Categorias de log
 * @type {[{id: number, descricao: string}]}
 */
export const categorias = [
    { id: 1, descricao: "Eventos de motorista" },
    { id: 2, descricao: "Cadastro de alvos" },
    { id: 3, descricao: "Cadastro de empresa" },
    { id: 4, descricao: "Cadastro de motorista" },
    { id: 5, descricao: "Cadastro de veículo" },
    { id: 6, descricao: "Cadastro de escala" },
    { id: 7, descricao: "Afastamento de motoristas" },
    { id: 8, descricao: "Tipo de afastamento" },
    { id: 9, descricao: "Não conformidades" },
    { id: 10, descricao: "Tempo de espera" },
    { id: 11, descricao: "Reprocesso" },
    { id: 12, descricao: "Configuração de horas do cartão ponto" },
    { id: 13, descricao: "Feriados" },
    { id: 14, descricao: "Faixa de horário" },
    { id: 15, descricao: "Troca de escala" },
    { id: 16, descricao: "Marcar DSR/feriado" },
    { id: 17, descricao: "Configuração de macro" },
    { id: 18, descricao: "Associações motorista-veículo" },
    { id: 19, descricao: "Importação de motoristas" },
    { id: 20, descricao: "Importação de veículos" },
    { id: 21, descricao: "Regras de horas do cartão ponto" },
    { id: 22, descricao: "Vinculação de alvo à filial" },
    // {id: 23, descricao: 'Processo de E-mail'},
    { id: 24, descricao: "Troca de período" },
    { id: 25, descricao: "Faixa de horário de alvo" },
    { id: 26, descricao: "Agendamento de associação" },
    { id: 27, descricao: "Desbloqueio de Jornadas" },
    { id: 28, descricao: "Bloqueio de Jornadas" },
    { id: 29, descricao: "Dispositivos Liberados" },
    { id: 30, descricao: "Assinatura Eletronica" },
    { id: 31, descricao: "Lançamentos Banco de Horas" },
    { id: 32, descricao: "Ativações do Motorista" },
    { id: 33, descricao: "Alterações pelo Aplicativo" },
    { id: 34, descricao: "Anomalias" },
    { id: 35, descricao: "Diárias" },
    { id: 36, descricao: "Recálculo de Não Conformidades" },
    { id: 37, descricao: "Períodos de BH" },
    { id: 38, descricao: "Clonagem de Escala" },
    { id: 39, descricao: "Configuracão Macro/Evento RFID" },
    { id: 40, descricao: "Configurações do Grid" },
    { id: 41, descricao: "Anotações" },
    { id: 42, descricao: "Ações Árvore de Decisão" },
    { id: 43, descricao: "Áreas Responsáveis Árvore de Decisão" },
    { id: 44, descricao: "Respostas Árvore de Decisão" },
    { id: 45, descricao: "Ações Árvore de Decisão" },
    { id: 46, descricao: "Resultados Árvore de Decisão" },
    { id: 47, descricao: "Níveis Árvore de Decisão" },
    { id: 48, descricao: "Configuração Árvore de Decisão" },
    { id: 49, descricao: "Fechamento de períodos" },
    {
        id: 50,
        descricao: "Config. cartão ponto - Config. gerais",
    },
    {
        id: 51,
        descricao: "Config. cartão ponto - Conjuntos colunas",
    },
    { id: 52, descricao: "Config. cartão ponto - Colunas" },
    { id: 53, descricao: "Config. cartão ponto - Totalizadores" },
    {
        id: 54,
        descricao: "Importação de template",
    },
    { id: 55, descricao: "Usuarios do sistema" },
    { id: 56, descricao: "Permissões do sistema" },
    { id: 57, descricao: "Vinculos empresa plano" },
    { id: 58, descricao: "Planos" },
    { id: 59, descricao: "Permissões do sistema por usuário" },
    { id: 60, descricao: "Apuração de jornada em massa" },
    { id: 61, descricao: "Importação de posições" },
    { id: 62, descricao: "Deleção de posições" },
    { id: 63, descricao: "Importação de macros" },
    { id: 64, descricao: "Deleção de macros" },
    { id: 65, descricao: "Observações da empresa" },
    { id: 66, descricao: "Apuração de jornada" },
    { id: 67, descricao: "Adiamento de notificações de exceções" },
    { id: 68, descricao: "Compensação de Horas" },
    { id: 68, descricao: "Serviços jornada" },
    { id: 69, descricao: "Impressão do Cartão Ponto" },
    { id: 70, descricao: "Aplicação de evento sugerido da jornada sugerida" },
    { id: 71, descricao: "Rejeição de evento sugerido da jornada sugerida" },
    { id: 72, descricao: "Rejeição de jornada sugerida" },
    { id: 73, descricao: "Aceite de jornada sugerida" },
    { id: 74, descricao: "Recálculo de associações" },
    { id: 75, descricao: "Criação de partições retroativas" },
    { id: 76, descricao: "Novidades do sistema" },
    { id: 77, descricao: "Aplicação de alterações via APP" },
    { id: 78, descricao: "Ocorrências da Jornada Assistida" },
];

/**
 * @param {string} string - Remove os acentos do texto
 * @return {string}
 */
export const removerAcentos = (string) =>
    string.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const validateEmail = (email) => {
    const re =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return re.test(email);
};

/**
 * Adiciona a classe que oculta o calendário da seleção
 * @param {{component: dxPopup}} e
 */
export const hideCalendarDropdown = (e) => {
    const content = e.component.content();
    // noinspection JSUnresolvedVariable
    content.parentNode.classList.add("date-box-dropdown-without-calendar");
};

export const editorOptionsDatas = {
    dropDownOptions: {
        onShowing: (e) => hideCalendarDropdown(e),
    },
    openOnFieldClick: true,
    dateSerializationFormat: "yyyy-MM-ddTHH:mm:ss",
    displayFormat: "HH:mm",
    useMaskBehavior: true,
};

export const validateCpf = (cpf) => {
    const test = /^\d{11}$/.test(cpf);

    if (!test) {
        return false;
    }

    const numbers = cpf.split("").map((n) => parseInt(n, 10));

    const dedupNumbers = new Set(numbers);

    if (numbers.length !== 11 || dedupNumbers.size === 1) {
        return false;
    }

    const sumOfProductsFirstDigit = numbers
        .slice(0, 9)
        .reduce((acc, num, i) => acc + num * (10 - i), 0);

    let firstDigit = (sumOfProductsFirstDigit * 10) % 11;

    if (firstDigit === 10) firstDigit = 0;

    if (firstDigit !== numbers[9]) {
        return false;
    }

    const sumOfProductsSecondDigit = numbers
        .slice(0, 10)
        .reduce((acc, num, i) => acc + num * (11 - i), 0);

    let secondDigit = (sumOfProductsSecondDigit * 10) % 11;

    if (secondDigit === 10) secondDigit = 0;

    if (secondDigit !== numbers[10]) {
        return false;
    }

    return true;
};

/**
 * @param {string} vh
 * @returns {number}
 */
export const converteVhParaPx = (vh) =>
    (parseInt(vh.replace("vh", "")) * window.innerHeight) / 100;

export const exportXlsx = async (
    component,
    sheetName,
    fileName,
    customOptions = {},
    additionalWorksheetConfigurationCallback = async (
        worksheet,
        component,
    ) => {},
) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    await additionalWorksheetConfigurationCallback(worksheet, component);

    exportDataGridToExcel({
        worksheet: worksheet,
        component: component,
        ...customOptions,
    }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
                new Blob([buffer], { type: "application/octet-stream" }),
                `${fileName}.xlsx`,
            );
        });
    });
};

export const exportCsv = (component, sheetName, fileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    exportDataGridToExcel({
        worksheet: worksheet,
        component: component,
    }).then(() => {
        workbook.csv.writeBuffer().then((buffer) => {
            saveAs(
                new Blob([buffer], { type: "application/octet-stream" }),
                `${fileName}.csv`,
            );
        });
    });
};

export const exportPdf = async (
    component,
    fileName,
    header,
    companyIdForGettingCustomLogo = null,
) => {
    const document = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "A3",
    });

    let lineSpacing = 5;
    const margin = 15;
    const marginHeaderBottom = 5;
    const logoWidth = 50;
    const logoHeight = 20;
    if (header) {
        let imgData =
            "data:image/png;base64,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"; // Your base64 string

        const { data: customLogoBase64ByHostname } = await api.get(
            "/user-theme/custom-logo-base64-by-hostname/",
            {
                params: {
                    hostname: window.location.hostname,
                },
            },
        );

        if (customLogoBase64ByHostname) {
            imgData = customLogoBase64ByHostname;
        }

        const { data: customLogoBase64 } = await api.get(
            "/user-theme/custom-logo-base64/",
            {
                params: {
                    company_id: companyIdForGettingCustomLogo,
                },
            },
        );

        if (customLogoBase64) {
            imgData = customLogoBase64;
        }

        document.addImage(
            imgData,
            "PNG",
            margin,
            margin,
            logoWidth,
            logoHeight,
        );

        for (let line in header) {
            let linePaddingTop = margin + lineSpacing * line;

            const title = header[line];
            const fontSize = 12;
            document.setFontSize(fontSize);

            const pageWidth = document.internal.pageSize.getWidth();
            const textWidth = document.getTextWidth(title);
            const centerX = (pageWidth - textWidth) / 2;

            document.text(title, centerX, linePaddingTop);
        }
    }

    exportDataGridToPdf({
        jsPDFDocument: document,
        component: component,
        autoTableOptions: {
            styles: {
                fontSize: 8,
            },
            ...(header && {
                margin: {
                    top: Math.max(
                        margin + lineSpacing * header.length,
                        margin + logoHeight + marginHeaderBottom,
                    ),
                },
            }),
        },
    }).then(() => {
        document.save(`${fileName}.pdf`);
    });
};

/**
 * @param {{}[]} lista - Lista de objetos
 * @param {string} chaveUnica - Nome da chave que deve ser usada para comparação
 * @return {{}[]}
 */
export const removerObjetosDuplicados = (lista, chaveUnica) =>
    lista.filter(
        (value, index, array) =>
            index ===
            array.findIndex((t) => t[chaveUnica] === value[chaveUnica]),
    );

// Remove a máscara do CNPJ mantendo apenas caracteres alfanuméricos e
// normaliza para maiúsculo. Suporta CNPJ numérico e alfanumérico.
export const normalizeCnpj = (value) =>
    (value || "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();

// Aplica a máscara AB.CDE.FGH/IJKL-MN. As 12 primeiras posições aceitam letras
// e dígitos; os 2 dígitos verificadores permanecem numéricos.
export const maskCnpj = (value) =>
    normalizeCnpj(value).replace(
        /^([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
    );

// Valida um CNPJ numérico ou alfanumérico (padrão Receita Federal): valor de
// cada caractere = código ASCII - 48 (dígitos 0-9 valem 0-9; letras A-Z valem
// 17-42). Os 2 dígitos verificadores permanecem numéricos. CNPJ numérico
// continua validando de forma idêntica ao algoritmo tradicional.
export const validateCnpj = (value) => {
    const cnpj = normalizeCnpj(value);
    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) return false;
    if (/^(.)\1{13}$/.test(cnpj)) return false;

    const charValue = (c) => c.charCodeAt(0) - 48;
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    let sum = 0;
    for (let i = 0; i < 12; i++) sum += charValue(cnpj[i]) * weights1[i];
    let rest = sum % 11;
    const dv1 = rest < 2 ? 0 : 11 - rest;
    if (dv1 !== Number(cnpj[12])) return false;

    sum = 0;
    for (let i = 0; i < 13; i++) sum += charValue(cnpj[i]) * weights2[i];
    rest = sum % 11;
    const dv2 = rest < 2 ? 0 : 11 - rest;
    return dv2 === Number(cnpj[13]);
};

export const maskCpf = (value) =>
    (value ?? "")
        .replace(/\D/g, "")
        .replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");

export const maskPhone = (value) => {
    const digits = (value ?? "").replace(/\D/g, "");
    if (digits.length <= 10) {
        return digits.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, "($1) $2-$3").trim();
    }
    return digits.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, "($1) $2-$3").trim();
};

export const DriverEventSource = {
    BetweenAssociations: "BETWEEN_ASSOCIATIONS",
    Macro: "MACRO",
    Mobile: "MOBILE",
    Position: "POSITION",
    Unknown: "UNKNOWN",
    Web: "WEB",
};

export const containsAll = (target, filter) =>
    filter.every((value) => target.includes(value));

export const formatDiaria = (container, data) => {
    if (!data.value) return;

    const finalData =
        typeof data.value === "string" ? JSON.parse(data.value) : data.value;

    const uniqueTypes = [...new Set(finalData)];

    const text = convertDiariasLabel(uniqueTypes);

    container.textContent = text;

    const contentTooltip = $("<div></div>");
    new dxTooltip(contentTooltip, {
        target: container,
        showEvent: "dxhoverstart",
        hideEvent: "dxhoverend",
        position: "bottom",
        contentTemplate: (options) => $(options).html(`<div>${text}</div>`),
    });

    contentTooltip.appendTo(container);
};

export const TiposAlvo = [
    {
        item: 0,
        descricao: "Controle de Espera",
    },
    {
        item: 1,
        descricao: "Parado em Jornada",
    },
    {
        item: 2,
        descricao: "Alvo de Balsa",
    },
    {
        item: 3,
        descricao: "Alvo sem Controle",
    },
    {
        item: 4,
        descricao: "Ignora Diárias",
    },
    {
        item: 5,
        descricao: "Controla envio de macros",
    },
];

export const JourneyTypes = {
    DSR: "D",
    HOLIDAY: "F",
    ABSENCE: "A",
};

export const STATUS_ACEITE_JORNADA_LABELS = {
    PENDING: "Pendente",
    DENIED: "Recusada",
    APPROVED: "Aprovada",
    UNAVAILABLE: "—",
};

export const VALOR_ACEITE_JORNADA = {
    PENDING: 0,
    APPROVED: 1,
    DENIED: 2,
};

export const formatStatusAceiteJornada = (reviewStatus, journeyDate) => {
    if (reviewStatus) {
        return STATUS_ACEITE_JORNADA_LABELS[reviewStatus];
    }

    const today = new Date();

    if (journeyDate >= today) {
        return STATUS_ACEITE_JORNADA_LABELS.UNAVAILABLE;
    }

    return STATUS_ACEITE_JORNADA_LABELS.PENDING;
};

Object.defineProperty(String.prototype, "capitalize", {
    value: function () {
        return this.charAt(0).toUpperCase() + this.slice(1);
    },
    enumerable: false,
});

export const durationToMinutes = (duration) => {
    if (typeof duration !== "string") {
        return null;
    }

    const [hours, minutes] = duration.split(":").map(Number);

    return duration.includes("-") ? hours * 60 - minutes : hours * 60 + minutes;
};

export const applyMask = (mask, number) => {
    if (mask === "" || mask === undefined || mask === null) {
        return number;
    }

    let s = "" + number,
        r = "";
    for (let im = 0, is = 0; im < mask.length && is < s.length; im++) {
        r += mask.charAt(im) == "9" ? s.charAt(is++) : mask.charAt(im);
    }
    return r;
};

export const isValidPlate = (plate) => {
    const patterns = [
        /^[A-Z]{3}-\d{4}$/, // Brasil: ABC-1234
        /^[A-Z]{3}\d[A-Z]\d{2}$/, // Brasil Mercosul: BRA1S34
        /^[A-Z]{3}-\d{3}$/, // Argentina / Paraguai: ABC-123
        /^[A-Z]{2}\d{3}[A-Z]{2}$/, // Argentina Mercosul: AB123CD
        /^\d{4}-[A-Z]{3}$/, // Bolívia: 1234-ABC
        /^[A-Z]{2}\d{5}$/, // Bolívia Mercosul: AB12345
        /^[A-Z]{2}-[A-Z]{2}-\d{2}$/, // Chile: BB-WL-18
        /^[A-Z]{2}\d{4}-\d$/, // Chile: AB1234-5
        /^[A-Z]{4}\d{3}$/, // Paraguai Mercosul: ABCD123
        /^[A-Z]\d[A-Z]-\d{3}$/, // Peru: A1A-700
        /^[A-Z]{2}\d-\d{3}$/, // Peru: AB1-234
    ];

    return patterns.some((pattern) => pattern.test(plate));
};

/**
 * @param {int} kmdiario
 * @return {number|string}
 */
export const validaKmDiario = (kmdiario) => {
    if (kmdiario !== null && kmdiario !== undefined) {
        return kmdiario.toString();
    } else {
        return "Não calculado";
    }
};

export const isEqual = (event1, event2) => {
    return (
        event1.start.replace("T", " ") == event2.joh_datahorainicial &&
        event1.end?.replace("T", " ") == event2.joh_datahorafinal &&
        event1.type == event2.joh_jevcodigo
    );
};

export const HHMMToMinutes = (string) => {
    const [hours, minutes] = string.split(":").map(Number);

    return hours * 60 + minutes;
};
